import PATHS, {
  APPLICATION_SUBMISSION_PATHS,
  SELF_IDENTIFICATION_PATHS,
  CREDIT_CRIMINAL_CHECK_PATHS,
  IDENTIFYING_DETAILS_PATHS,
} from 'constants/paths';
import SECTION_NAMES from 'constants/sectionNames';
import useBaseURL from 'hooks/useBaseURL';
import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useHouseholdMembersContext } from 'contexts/HouseholdMembersContext';
import { useSectionsProgressContext } from 'contexts/SectionsProgressContext';
import useEnableUnInvitedSecondaryUserRouteLogging from 'hooks/dev/useEnableUnInvitedSecondaryUserRouteLogging';
import debugLogger from 'utils/logger';

const useUnInvitedSecondaryUserRouteManager = ({
  sectionsProgress,
  isSectionsProgressInitialized,
}) => {
  const history = useHistory();
  const baseURL = useBaseURL();
  const { isUnInvitedSecondaryUserRouteManagerLoggingEnabled } =
    useEnableUnInvitedSecondaryUserRouteLogging();

  const { isUnInvitedSecondaryUser, hasData } = useHouseholdMembersContext();
  const { allSectionsAreComplete } = useSectionsProgressContext();

  const logger = useCallback(
    (message, data, status) => {
      if (!isUnInvitedSecondaryUserRouteManagerLoggingEnabled) return;
      debugLogger('RouteManager', message, data, status);
    },
    [isUnInvitedSecondaryUserRouteManagerLoggingEnabled],
  );

  logger('Initialized with state', {
    isUnInvitedSecondaryUser,
    hasData,
    isSectionsProgressInitialized,
    allSectionsAreComplete,
    sectionsProgress,
  });

  const {
    [SECTION_NAMES.IDENTIFYING_DETAILS]: identifyingDetailsProgress,
    [SECTION_NAMES.CREDIT_CRIMINAL_CHECK]: creditCriminalCheckProgress,
    [SECTION_NAMES.SELF_IDENTIFICATION]: selfIdentificationProgress,
    [SECTION_NAMES.APPLICATION_SUBMISSION]: applicationSubmissionProgress,
  } = sectionsProgress;

  useEffect(() => {
    logger('useEffect triggered', {
      isSectionsProgressInitialized,
      isUnInvitedSecondaryUser,
      hasData,
      allSectionsAreComplete,
    });

    if (
      !isSectionsProgressInitialized ||
      !isUnInvitedSecondaryUser ||
      !hasData
    ) {
      logger('Early return - prerequisites not met', {
        isSectionsProgressInitialized,
        isUnInvitedSecondaryUser,
        hasData,
      });
      return;
    }

    if (!allSectionsAreComplete) {
      // These steps are ordered sequentially
      if (!identifyingDetailsProgress?.isComplete) {
        const redirectPath = `${baseURL}/${PATHS.IDENTIFYING_DETAILS}/${IDENTIFYING_DETAILS_PATHS.IDENTIFICATION}`;
        logger('Redirecting to Identifying Details', {
          from: history.location.pathname,
          to: redirectPath,
          identifyingDetailsProgress,
        });
        return history.push(redirectPath);
      }
      if (!creditCriminalCheckProgress?.isComplete) {
        const redirectPath = `${baseURL}/${PATHS.CREDIT_CRIMINAL_CHECK}/${CREDIT_CRIMINAL_CHECK_PATHS.CONSENT}`;
        logger('Redirecting to Credit Criminal Check', {
          from: history.location.pathname,
          to: redirectPath,
          creditCriminalCheckProgress,
        });
        return history.push(redirectPath);
      }
      if (!selfIdentificationProgress?.isComplete) {
        const redirectPath = `${baseURL}/${PATHS.SELF_IDENTIFICATION}/${SELF_IDENTIFICATION_PATHS.VOLUNTARY_SELF_IDENTIFICATION}`;
        logger('Redirecting to Self Identification', {
          from: history.location.pathname,
          to: redirectPath,
          selfIdentificationProgress,
        });
        return history.push(redirectPath);
      }
      if (!applicationSubmissionProgress?.isComplete) {
        const redirectPath = `${baseURL}/${PATHS.APPLICATION_SUBMISSION}/${APPLICATION_SUBMISSION_PATHS.SIGNATURE_AND_REPRESENTATION}`;
        logger('Redirecting to Application Submission', {
          from: history.location.pathname,
          to: redirectPath,
          applicationSubmissionProgress,
        });
        return history.push(redirectPath);
      }
    } else {
      logger('All sections complete, no redirect needed', {
        allSectionsAreComplete,
      });
    }
  }, [
    baseURL,
    history,
    isUnInvitedSecondaryUser,
    identifyingDetailsProgress,
    creditCriminalCheckProgress,
    applicationSubmissionProgress,
    selfIdentificationProgress?.isComplete,
    selfIdentificationProgress,
    sectionsProgress,
    isSectionsProgressInitialized,
    hasData,
    allSectionsAreComplete,
    logger,
  ]);
};

export default useUnInvitedSecondaryUserRouteManager;
